<template>
  <NuxtImg
    v-if="!hasError"
    ref="imgRef"
    :src="finalSrc"
    :srcset="finalSrcset || undefined"
    :sizes="resolvedSizes"
    :alt="computedAlt"
    :loading="loading"
    :width="mediaWidth"
    :height="mediaHeight"
    :style="placeholderStyle"
    @load="onLoad"
    @error="onError"
    class="block"
  />
  <img
    v-else
    :src="placeholderSrc"
    :alt="alt"
    :loading="loading"
    :width="mediaWidth || 800"
    :height="mediaHeight || 600"
    class="grayscale object-cover object-center h-full w-full overflow-hidden block"
  />
</template>

<script setup>
const props = defineProps({
  src: { type: String, required: false, default: "" },
  media: { type: Object, default: null },
  alt: { type: String, default: "" },
  loading: { type: String, default: "lazy" },
  srcset: {
    type: [Array, String, null],
    default: null,
  },
  sizes: { type: String, default: null },
  sizeHint: {
    type: String,
    default: "full",
  },
  formatOrder: {
    type: Array,
    default: () => [
      "xlarge_webp",
      "large_webp",
      "medium_webp",
      "small_webp",
      "xlarge",
      "large",
      "medium",
      "small",
      "thumbnail",
    ],
  },
});

const runtimeConfig = useRuntimeConfig();
const baseUrl = runtimeConfig.public.baseUrl;

const placeholderSrc = "/img/placeholder-img.webp";
const hasError = ref(false);
const isLoaded = ref(false);

const sizeMap = {
  full: "(max-width: 1024px) 100vw, 100vw",
  content: "(max-width: 1024px) 100vw, 70vw",
  half: "(max-width: 1024px) 100vw, 50vw",
  third: "(max-width: 1024px) 100vw, 33vw",
  quarter: "(max-width: 1024px) 100vw, 25vw",
  sidebar: "(max-width: 1024px) 100vw, 320px",
  logo: "(max-width: 1024px) 80vw, 220px",
  icon: "96px",
};

const buildAbsoluteSrc = (value) => {
  if (!value) return "";
  return value.startsWith("http") || value.startsWith("data:") ? value : `${baseUrl}${value}`;
};

const preferredAltKeys = ["alternativeText", "name", "hash", "caption", "ext"];

const normalizeSrcsetEntry = (entry) => {
  if (!entry?.src) return "";
  const descriptor = entry.descriptor || entry.width;
  const suffix = descriptor ? ` ${descriptor}` : "";
  return `${buildAbsoluteSrc(entry.src)}${suffix}`.trim();
};

const isWebpUrl = (url) => /\.webp($|\?)/i.test(url);

const mediaCandidates = computed(() => {
  if (!props.media) return [];

  const formats = props.media.formats || {};
  const candidates = [];
  const added = new Set();

  props.formatOrder.forEach((size, index) => {
    const isExplicitWebp = size.endsWith("_webp");
    const baseSize = isExplicitWebp ? size.replace(/_webp$/, "") : size;
    const entries = [
      formats[`${baseSize}_webp`],
      formats[isExplicitWebp ? size : baseSize],
    ].filter(Boolean);

    entries.forEach((format, entryIndex) => {
      if (!format?.url || added.has(format.url)) return;
      added.add(format.url);
      candidates.push({
        src: format.url,
        descriptor: format.width ? `${format.width}w` : undefined,
        width: format.width || 0,
        priority: index * 2 + entryIndex,
        isWebp: isWebpUrl(format.url),
      });
    });
  });

  if (props.media.url && !added.has(props.media.url)) {
    const originalWidth = props.media.width || 0;
    // Eğer orijinal görsel > 2560px ise ve halihazırda xlarge gibi formatlar (candidates) üretilmişse,
    // orijinal devasa dosyayı srcset'e ekleme. Böylece tarayıcı mecburen xlarge (1900w vs) formatı kullanır.
    if (originalWidth <= 2560 || candidates.length === 0) {
      candidates.push({
        src: props.media.url,
        descriptor: props.media.width ? `${props.media.width}w` : undefined,
        width: props.media.width || 0,
        priority: props.formatOrder.length * 2,
        isWebp: isWebpUrl(props.media.url),
      });
    }
  }

  return candidates.sort((a, b) => {
    if (a.width === b.width) return a.priority - b.priority;
    return a.width - b.width;
  });
});

const computedAlt = computed(() => {
  if (props.alt) return props.alt;
  if (!props.media) return "";

  for (const key of preferredAltKeys) {
    if (props.media[key]) {
      return props.media[key];
    }
  }

  return "";
});

const autoSrcset = computed(() => {
  if (!mediaCandidates.value.length) return "";
  return mediaCandidates.value
    .map(({ src, descriptor }) => ({ src, descriptor }))
    .map(normalizeSrcsetEntry)
    .filter(Boolean)
    .join(", ");
});

const finalSrcset = computed(() => {
  if (props.srcset) {
    if (typeof props.srcset === "string") return props.srcset;
    if (Array.isArray(props.srcset)) {
      return props.srcset.map(normalizeSrcsetEntry).filter(Boolean).join(", ");
    }
  }
  return autoSrcset.value;
});

const resolvedSizes = computed(() => {
  if (props.sizes) return props.sizes;
  return sizeMap[props.sizeHint] || sizeMap.full;
});

const finalSrc = computed(() => {
  if (props.src) return buildAbsoluteSrc(props.src);

  if (mediaCandidates.value.length) {
    const pickFrom = [...mediaCandidates.value].sort((a, b) => {
      if (a.width === b.width) return a.priority - b.priority;
      return b.width - a.width;
    });

    const preferred =
      pickFrom.find((candidate) => candidate.isWebp) || pickFrom[0];
    return buildAbsoluteSrc(preferred?.src);
  }

  if (props.media?.url) return buildAbsoluteSrc(props.media.url);

  return "";
});

const mediaWidth = computed(() => props.media?.width || undefined);
const mediaHeight = computed(() => props.media?.height || undefined);

const placeholderStyle = computed(() => ({
  backgroundColor: isLoaded.value || props.sizeHint === "icon" ? "transparent" : "#f3f4f6",
}));

watch(finalSrc, () => {
  isLoaded.value = false;
  hasError.value = false;
});

const onLoad = () => {
  isLoaded.value = true;
};

const onError = () => {
  hasError.value = true;
  isLoaded.value = false;
};
</script>
